import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { ICircular, ICircularForm } from 'models/circular_model';
import {
  getCircularesService,
  createCircularService,
  editCircularService,
  deleteCircularService,
} from 'repositories/circular_repository';

interface CircularState {
  circulares: ICircular[];
  count: number;
  loading: boolean;
  error: string | null;
}

const initialState: CircularState = {
  circulares: [],
  count: 0,
  loading: false,
  error: null,
};

export const cargarCirculares = createAsyncThunk(
  'circular/cargarCirculares',
  async (
    params: {
      limit: number;
      page: number;
      codigoFilter?: string;
      asuntoFilter?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { rows, count } = await getCircularesService({
        codigo: params.codigoFilter,
        asunto: params.asuntoFilter,
        limit: params.limit,
        page: params.page,
      });
      return { rows: rows ?? [], count: count ?? 0 };
    } catch {
      return rejectWithValue('Error al cargar circulares');
    }
  },
);

export const addCircular = createAsyncThunk(
  'circular/addCircular',
  async (data: ICircularForm, { rejectWithValue }) => {
    try {
      await createCircularService(data);
    } catch {
      return rejectWithValue('Error al crear circular');
    }
  },
);

export const editCircular = createAsyncThunk(
  'circular/editCircular',
  async ({ data, userId }: { data: ICircularForm; userId: string }, { rejectWithValue }) => {
    try {
      await editCircularService(data, userId);
    } catch {
      return rejectWithValue('Error al editar circular');
    }
  },
);

export const deleteCircular = createAsyncThunk(
  'circular/deleteCircular',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteCircularService(id);
    } catch {
      return rejectWithValue('Error al eliminar circular');
    }
  },
);

export const circularSlice = createSlice({
  name: 'circular',
  initialState,
  reducers: {
    setCirculares: (state, action: PayloadAction<{ rows: ICircular[]; count: number }>) => {
      state.circulares = action.payload.rows;
      state.count = action.payload.count;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarCirculares.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarCirculares.fulfilled, (state, action) => {
        state.loading = false;
        state.circulares = action.payload.rows;
        state.count = action.payload.count;
      })
      .addCase(cargarCirculares.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCirculares } = circularSlice.actions;
export default circularSlice.reducer;
